import type { FieldType } from "@/schemas/digital-files";
import {
  GroupTextField,
  GroupTextFieldRow,
} from "@elaniintc/goes-design-system";
import { FormOptions } from "./OptionsDropdown";
import axios from "@/utils/axios";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useRegistryForm } from "../contexts/RegistryFormContext";
import {
  validateEmail,
  validateRequired,
} from "./RegistryFormField.validation";
import { getFieldByType } from "./RegistryFormField.renderers";

interface RegistryFormFieldProps {
  field: FieldType;
  value: unknown;
  optionValue: string;
  onChange: (value: unknown) => void;
  onOptionChange: (
    fieldId: string,
    option: string,
    label?: string,
    table?: string,
    isDataCenter?: boolean,
  ) => void;
}

export const RegistryFormField = ({
  field,
  value,
  optionValue,
  onChange,
  onOptionChange,
}: RegistryFormFieldProps) => {
  const [localError, setLocalError] = useState<string | undefined>();
  const { setFieldError, touchedFields, setFieldTouched } = useRegistryForm();
  const Field = getFieldByType(field, value, onChange, () =>
    setFieldTouched(field.id, true),
  );
  const [options, setOptions] = useState<
    { key: string; label: string; table?: string }[]
  >(
    field.configuration?.options_dropdown?.map((option) => ({
      key: option,
      label: option,
    })) ?? [],
  );

  // Validación en useEffect para evitar loops infinitos
  useEffect(() => {
    let error: string | undefined;

    if (field.is_required) {
      error = validateRequired(field, value);
    }

    if (!error && field.field_type === "EMAIL" && value) {
      error = validateEmail(value as string, field.configuration);
    }

    setLocalError(error);
    setFieldError(field.id, error);
  }, [value, field, setFieldError]);

  const shouldShowError = touchedFields[field.id] ? localError : undefined;

  const OptionsComponentAlign = field.configuration?.buttonPosition || "left";
  const hasDataCenterConfig =
    !!field.configuration?.options_table &&
    !!field.configuration?.options_column;
  const hasLocalOptions =
    field.configuration?.options && field.configuration?.options.length > 0;

  const shouldShowOptions =
    field.configuration?.buttonEnabled &&
    (hasLocalOptions || hasDataCenterConfig);

  // Buscar la opción seleccionada usando comparación case-insensitive
  const currentOptionValue = useMemo(() => {
    const selected = options.find(
      (opt) =>
        String(opt.key).toLowerCase() ===
        String(optionValue || "").toLowerCase(),
    );

    return (
      selected ||
      (options.length > 0
        ? options[0]
        : { key: "", label: "", table: undefined })
    );
  }, [options, optionValue]);

  useEffect(() => {
    const fetchOptionsFromDataCenter = async () => {
      if (hasDataCenterConfig) {
        try {
          const table_name = field.configuration?.options_table;
          const column_name = field.configuration?.options_column;

          if (!table_name || !column_name) return;

          const response = await axios.get(
            "/admin/v1/data-center/tables/data",
            {
              params: {
                table_name,
              },
            },
          );
          const data =
            response.data.data?.map(
              (item: { [key: string]: string | number }) => ({
                key: item.id,
                label: item[column_name],
                table: table_name,
              }),
            ) ?? [];
          setOptions(data);
        } catch (error) {
          console.error(`Error fetching data from table:`, error);
          setOptions([]);
        }
      }
    };
    fetchOptionsFromDataCenter();
  }, [
    hasDataCenterConfig,
    field.configuration?.options_table,
    field.configuration?.options_column,
  ]);

  const handleOptionSelect = useCallback(
    (option: string, table?: string) => {
      const selectedOption = options.find((opt) => opt.key === option);
      onOptionChange(
        field.id,
        option,
        selectedOption?.label,
        table,
        hasDataCenterConfig,
      );
    },
    [field.id, onOptionChange, options, hasDataCenterConfig],
  );

  const OptionsComponent = useMemo(
    () =>
      shouldShowOptions && (
        <FormOptions
          options={options}
          selectedOption={currentOptionValue}
          onOptionSelect={handleOptionSelect}
        />
      ),
    [shouldShowOptions, options, currentOptionValue, handleOptionSelect],
  );

  return (
    <GroupTextField
      key={field.id}
      label={field.name}
      required={field.is_required}
      error={shouldShowError}
    >
      <GroupTextFieldRow className={"h-auto"}>
        {OptionsComponentAlign === "left" && OptionsComponent}
        {Field}
        {OptionsComponentAlign === "right" && OptionsComponent}
      </GroupTextFieldRow>
    </GroupTextField>
  );
};
